import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate , useSearchParams} from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Link2, Plus, Trash2, PlayCircle, Wifi, WifiOff,
  Brain, Phone, MessageCircle, Mail, CreditCard, Server, ChevronRight, ChevronDown,
  X, RefreshCw, Zap, Loader2, Settings
} from 'lucide-react';
import EvolutionConnectModal from '@/components/integrations/EvolutionConnectModal';
import MetaWhatsappEmbeddedSignup from '@/components/integrations/MetaWhatsappEmbeddedSignup';
import MetaFanPageConnect from '@/components/integrations/MetaFanPageConnect';
import InstagramLoginConnect from '@/components/integrations/InstagramLoginConnect';
import TikTokLoginConnect from '@/components/integrations/TikTokLoginConnect';
import LinkedInLoginConnect from '@/components/integrations/LinkedInLoginConnect';
import LinkedInPageConnect from '@/components/integrations/LinkedInPageConnect';
import FieldHelp from '@/components/help/FieldHelp';

// Sprint 34 — ayuda coloquial por campo, para que conectar Twilio Voz no se sienta técnico.
const CREDENTIAL_FIELD_HELP = {
  'twilio_voice.account_sid': 'Es como el "número de cuenta" de tu Twilio. Lo encuentras en tu panel de Twilio (twilio.com/console), justo en la página principal — empieza con "AC".',
  'twilio_voice.auth_token': 'Es la "contraseña" de tu cuenta de Twilio para que nuestro sistema pueda usarla. Está justo al lado del Account SID en tu panel de Twilio — hay un botón para revelarla.',
  'twilio_voice.from_number': 'Cópialo de Twilio → Phone Numbers → My Inventory. Ejemplo: +525599628004. Con el + y sin espacios. Es el número del puente cliente↔vendedor y de las llamadas.',
  'twilio_voice.webhook_auth_token': 'Opcional — solo si tu equipo técnico te pidió configurar una capa extra de seguridad para las llamadas. Si no sabes qué es esto, puedes dejarlo en blanco.',
  'twilio_whatsapp.account_sid': 'Es como el "número de cuenta" de tu Twilio. Lo encuentras en tu panel de Twilio (twilio.com/console), justo en la página principal — empieza con "AC".',
  'twilio_whatsapp.auth_token': 'Es la "contraseña" de tu cuenta de Twilio para que nuestro sistema pueda usarla. Está justo al lado del Account SID en tu panel de Twilio.',
  'meta_whatsapp.phone_number_id': 'Es el identificador del número de WhatsApp en Meta. Lo ves en developers.facebook.com → tu app → WhatsApp → API Setup, en la sección "Phone number ID".',
  'meta_whatsapp.access_token': 'Token permanente de acceso a la API de WhatsApp. Lo generas en el panel de Meta (System User o token de la app). No lo compartas con nadie.',
  'meta_whatsapp.business_id': 'Opcional — ID de tu cuenta de WhatsApp Business (WABA). Ayuda a gestionar plantillas; si conectas con el botón de Meta, se llena solo.',
  'meta_whatsapp.verify_token': 'Opcional — frase secreta que tú inventas para que Meta confirme que los webhooks vienen de ellos. Si no sabes qué poner, déjalo vacío por ahora.',
  'gemini.api_key': 'Llave de API de Google AI Studio (aistudio.google.com). Empieza con "AIza". La IA usará esta cuenta para responder mensajes cuando elijas un modelo Gemini.',
  'azure_openai.api_key': 'Llave de tu recurso de Azure OpenAI. La encuentras en Azure Portal → tu recurso → Keys and Endpoint.',
  'azure_openai.endpoint': 'URL del recurso, ej. https://mi-recurso.openai.azure.com — está en Azure Portal junto a las llaves.',
  'azure_openai.deployment_name': 'Nombre del "deployment" que creaste en Azure (no el nombre del modelo). Es el identificador que usa tu recurso para las llamadas.',
  'azure_openai.api_version': 'Opcional — versión de la API de Azure. Si no la conoces, deja el valor sugerido (2024-02-01).',
  'ses.access_key_id': 'Clave de acceso de AWS IAM con permiso para enviar correo con SES. La creas en la consola de AWS → IAM.',
  'ses.secret_access_key': 'Contraseña secreta de esa clave IAM. Solo se muestra una vez al crearla en AWS.',
  'ses.region': 'Región de AWS donde tienes SES activo, ej. us-east-1. Debe coincidir con donde verificaste tu dominio o correo.',
  'ses.from_email': 'Correo remitente verificado en SES. Los destinatarios verán este remitente — debe estar autorizado en tu cuenta AWS.',
  'cohere.api_key': 'Llave de API de Cohere (dashboard.cohere.com). La IA usará esta cuenta cuando elijas un modelo Cohere en tu configuración.',
  'gupshup.api_key': 'API Key de tu app en Gupshup (partner.gupshup.io → tu app → Settings).',
  'gupshup.app_name': 'Nombre exacto de la app en Gupshup — debe coincidir con el campo "app" que Gupshup envía en el webhook.',
  'gupshup.source_phone': 'El número de WhatsApp de tu negocio en Gupshup (solo dígitos con código de país, ej. 5215512345678). Es el remitente para enviar mensajes, plantillas y recibir respuestas.',
};

// Fallback when DB provider row is stale (seed sync lag) — tenant form must
// always collect sender number for Gupshup outbound + webhook routing.
const CREDENTIAL_SCHEMA_OVERRIDES = {
  twilio_voice: [
    { key: 'account_sid', label: 'Account SID (empieza con AC)', type: 'text', required: true, placeholder: 'ACxxxxxxxx' },
    { key: 'auth_token', label: 'Auth Token (contraseña de la cuenta)', type: 'password', required: true },
    { key: 'from_number', label: 'Número de voz de Twilio (con +)', type: 'text', required: true, placeholder: '+525599628004' },
    { key: 'webhook_auth_token', label: 'Webhook Auth Token (opcional — déjalo vacío)', type: 'password', required: false },
  ],
  gupshup: [
    { key: 'api_key', label: 'API Key', type: 'password', required: true },
    { key: 'app_id', label: 'App ID', type: 'text', required: false },
    { key: 'app_name', label: 'App Name (src.name)', type: 'text', required: true },
    { key: 'source_phone', label: 'Número WhatsApp del negocio', type: 'text', required: true, placeholder: '5215512345678' },
  ],
};

const resolveCredentialSchema = (item) => {
  const key = item?.key || item?.provider_key;
  if (key && CREDENTIAL_SCHEMA_OVERRIDES[key]) return CREDENTIAL_SCHEMA_OVERRIDES[key];
  return item?.credential_schema || [];
};

const CATEGORY_ICONS = {
  ai: Brain, voice: Phone, voice_telephony: Phone, voice_ai: Brain,
  whatsapp: MessageCircle, email: Mail, payment: CreditCard, mcp: Server, social: MessageCircle,
};
// OAuth-only — never use the generic credential form; each has its own connect UI.
const OAUTH_ONLY_PROVIDERS = new Set(['meta_page', 'instagram_login', 'meta_ads', 'tiktok_login', 'linkedin_login', 'linkedin_page']);

const webhookPathFor = (integration) => {
  if (integration?.webhook_url) return integration.webhook_url;
  const paths = {
    meta_whatsapp: '/api/webhooks/meta',
    gupshup: '/api/webhooks/gupshup',
    twilio_whatsapp: `/api/webhooks/twilio-whatsapp/${integration.id}`,
    evolution_api: `/api/webhooks/evolution/${integration.id}`,
  };
  return paths[integration.provider_key] || null;
};

const statusBadgeClass = (status) => {
  if (status === 'connected') return 'bg-emerald-50 text-emerald-600';
  if (status === 'pending_setup') return 'bg-amber-50 text-amber-700';
  if (status === 'error') return 'bg-red-50 text-red-600';
  return 'bg-[#F1F5F9] text-[#94A3B8]';
};

const statusLabel = (status) => (status === 'pending_setup' ? 'configuración pendiente' : status);

const CATEGORY_LABELS = {
  ai: 'IA', voice: 'Voz', voice_telephony: 'Telefonía', voice_ai: 'Voz IA',
  whatsapp: 'WhatsApp / Mensajería', email: 'Email', payment: 'Pagos', mcp: 'MCP',
  social: 'Redes sociales',
};

const AI_PURPOSE_LABELS = {
  inbox_whatsapp: 'Responder WhatsApp',
  inbox_suggest: 'Sugerencias en Inbox',
  voice_calls: 'Llamadas con IA (Twilio Voice)',
  general_default: 'Asistente general',
};

const AI_PURPOSES_BY_CATEGORY = {
  ai: ['inbox_whatsapp', 'inbox_suggest', 'general_default'],
  voice_ai: ['voice_calls', 'general_default'],
};

// 2026-07-06 — restores the entrada/salida clarity that the deleted
// IntegrationPurposes.js used to show as a Badge per capability (removed
// when capabilities were folded into this page in Sprint 30, unnoticed
// until the platform owner pointed it out directly).
const DIRECTION_LABELS = { inbound: 'Entrada', outbound: 'Salida' };

// S43(2/N, 3/N) — same per-branch credential feature built for WhatsApp in
// 43(1/N), extended to phone connectivity (voice_telephony, e.g. Twilio
// Voice) and email. Deliberately excludes 'voice_ai' (speech AI providers
// like ElevenLabs/Deepgram) — those aren't a phone-number-per-branch concept.
const BRANCH_AWARE_CATEGORIES = ['whatsapp', 'voice_telephony', 'email'];
// Fase 2 (07-18) — proveedores donde el tenant puede tener 2+ conexiones
// reales al mismo tiempo (ej. un Twilio Voz para llamadas de leads, otro
// solo para avisos internos). Excluye Evolution API a propósito: su flujo
// de conexión vive en un modal QR aparte que todavía no admite una segunda
// instancia en esta entrega.
const MULTI_INSTANCE_PROVIDER_KEYS = ['twilio_voice', 'twilio_whatsapp', 'gupshup', 'meta_whatsapp'];

// Integrations that support per-connection capability assignment (Inbox, campañas, etc.)
const CAPABILITY_PANEL_CATEGORIES = new Set(['whatsapp', 'voice_telephony', 'email']);

const CHANNEL_TYPE_BY_CATEGORY = {
  whatsapp: 'whatsapp',
  voice_telephony: 'voice',
  email: 'email',
};

const supportsCapabilityPanel = (integration) => CAPABILITY_PANEL_CATEGORIES.has(integration.provider_category);

// 2026-07-06 — unlike Evolution (QR pairing, no external config needed),
// these providers require the tenant to paste our webhook URL into their
// OWN provider console by hand — something they have zero way of knowing
// to do unless told explicitly. Found this the hard way: a real tenant's
// Twilio WhatsApp number sat completely silent because nothing ever told
// them where to configure it.
const WEBHOOK_SETUP_GUIDES = {
  twilio_voice: {
    title: 'Opcional: solo si quieres llamadas ENTRANTES con IA',
    steps: [
      'El puente lead↔vendedor y las llamadas salientes YA funcionan solo con Account SID + Auth Token + número. No hace falta webhook.',
      'Si activas "Llamadas entrantes con IA" en Config IA → IA Voice, entonces sí: Numbers & senders → Phone Numbers → Inventory → clic en el número.',
      'Voice Configuration → A call comes in → Webhook + HTTP POST.',
      'Pega la URL que muestra IA Voice (misma: /api/webhooks/voice/inbound).',
      'Guarda. Si no vas a recibir llamadas al número, ignora este bloque.',
    ],
  },
  twilio_whatsapp: {
    title: 'Falta un paso en Twilio (fuera de esta app)',
    steps: [
      'Entra a la consola de Twilio (console.twilio.com) y abre tu WhatsApp Sender.',
      'Busca la sección "Messaging Endpoint Configuration".',
      'En el campo "Webhook URL for incoming messages" pega la URL de abajo.',
      'Verifica que el menú de al lado diga "HTTP Post".',
      'Baja hasta el final y presiona "Save WhatsApp Sender".',
    ],
  },
  meta_whatsapp: {
    title: 'Falta configurar el webhook en Meta (fuera de esta app)',
    steps: [
      'Entra a developers.facebook.com → tu app → WhatsApp → Configuration.',
      'En "Webhook" presiona "Edit" y pega la URL de abajo.',
      'En "Verify token" usa el token que configuró el administrador de la plataforma en Infraestructura.',
      'Suscríbete al campo "messages" y guarda.',
      'Vuelve aquí y presiona "Test" para confirmar que la conexión está activa.',
    ],
  },
  gupshup: {
    title: 'Pega esta URL en Gupshup',
    steps: [
      'Portal VIEJO (unified / www.gupshup.io): abre la app → Settings → Callback / Webhook y pega la URL de abajo.',
      'Portal NUEVO (gupshup.ai): en Detalles de la aplicación NO está. Busca Settings o Callback de ESA app. Si no aparece, en CavernicolApp ve a Infraestructura → Gupshup Partner, pega el App ID y pulsa Registrar webhook.',
      'App Name aquí debe ser exactamente el de Gupshup (ej. CavernicolaCreativo).',
      'Activa entrada de leads por WhatsApp en las capacidades de esta misma pantalla.',
    ],
  },
  evolution_api: {
    title: 'Evolution registra el webhook automáticamente al escanear el QR',
    steps: [
      'Abre esta integración y escanea el código QR con WhatsApp.',
      'Si reconectas, el sistema vuelve a registrar el webhook en Evolution.',
      'La URL de abajo es la que Evolution debe tener — úsala solo si configuras manualmente.',
      'Marca "Entrada de leads por WhatsApp" en las capacidades de abajo.',
    ],
  },
  // S48 (5/N) — same gap as Twilio WhatsApp above: without this, un pago
  // real nunca se confirma, porque Mercado Pago/Stripe no tienen forma de
  // saber a dónde avisarnos, y la clave para verificar que el aviso vino
  // de ellos (no de un impostor) solo existe en su propio panel.
  mercadopago: {
    title: 'Faltan 2 pasos en Mercado Pago (fuera de esta app)',
    steps: [
      'Entra a tu panel de Mercado Pago Developers > Tus aplicaciones > (tu app) > Webhooks.',
      'En "URL de producción" pega la URL de abajo y guarda.',
      'Copia la "Clave secreta" que te muestra Mercado Pago en esa misma pantalla.',
      'Vuelve aquí y pégala en el campo "Clave secreta de firma (Webhooks)" de esta conexión.',
    ],
  },
  stripe: {
    title: 'Falta 1 paso en Stripe (fuera de esta app)',
    steps: [
      'Entra a tu panel de Stripe > Desarrolladores > Webhooks > Agregar endpoint.',
      'En "Endpoint URL" pega la URL de abajo.',
      'En eventos a escuchar, selecciona "checkout.session.completed".',
      'Guarda, copia el "Signing secret" (empieza con whsec_) y pégalo aquí en el campo "Webhook Secret".',
    ],
  },
};

export default function IntegrationCenter() {
  const navigate = useNavigate();
  const location = useLocation();
  const [integrations, setIntegrations] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConnect, setShowConnect] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedProvider, setSelectedProvider] = useState(null);

  const [credForm, setCredForm] = useState({});
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showEvolutionModal, setShowEvolutionModal] = useState(false);
  const [aiPurposes, setAiPurposes] = useState(['inbox_whatsapp', 'general_default']);
  const [matrix, setMatrix] = useState([]);

  // S103 — deep link ?provider=gupshup|twilio_whatsapp|meta_whatsapp
  useEffect(() => {
    const pk = searchParams.get('provider');
    if (!pk || !matrix?.length) return;
    const found = matrix.find(m => (m.key || m.provider_key) === pk);
    if (found) {
      setSelectedProvider(found);
      setShowConnect(true);
      const next = new URLSearchParams(searchParams);
      next.delete('provider');
      setSearchParams(next, { replace: true });
    }
  }, [matrix, searchParams, setSearchParams]);

  const [expandedChannels, setExpandedChannels] = useState(null);
  const [provisioning, setProvisioning] = useState(null);
  const [capSaving, setCapSaving] = useState({});
  const [platformInfraEnabled, setPlatformInfraEnabled] = useState(false);
  const [usingPlatformInfra, setUsingPlatformInfra] = useState(null);
  // 2026-07-06 — the PUT /tenant/integrations/{id} endpoint always supported
  // updating credentials, but the detail view never exposed a form for it —
  // once connected, a provider (e.g. re-checking Twilio's SID/Token/number)
  // could only be fixed by fully disconnecting and reconnecting from scratch.
  const [editingCreds, setEditingCreds] = useState(false);
  const [editCredForm, setEditCredForm] = useState({});
  const [savingCreds, setSavingCreds] = useState(false);
  // S42(1/N) — per-branch WhatsApp credentials. Only relevant/shown once a
  // tenant actually has more than one sucursal; otherwise every field below
  // stays empty/hidden and behavior is identical to before this sprint.
  const [branches, setBranches] = useState([]);
  const [connectBranchId, setConnectBranchId] = useState('');
  // Fase 2 (07-18) — cuando el tenant ya tiene una conexión de este mismo
  // proveedor, ofrecer un nombre para la conexión adicional en vez de
  // sobrescribir la existente en silencio.
  const [connectInstanceLabel, setConnectInstanceLabel] = useState('');
  const [savingBranch, setSavingBranch] = useState(false);

  // CR-020: Read ?category= query param for deep-linking from Email Config
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get('category');
    if (cat) setCategoryFilter(cat);
  }, [location.search]);

  // OAuth round-trip from Meta/Instagram — return_to lands here but the
  // list view never handled ?oauth= before, so users thought it "failed".
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oauth = params.get('oauth');
    if (!oauth || !['meta', 'instagram', 'tiktok', 'linkedin'].includes(oauth)) return;

    const connected = params.get('connected') === '1';
    const providerKey = {meta: 'meta_page', instagram: 'instagram_login', tiktok: 'tiktok_login', linkedin: 'linkedin_login'}[oauth];
    const label = {meta: 'Meta', instagram: 'Instagram', tiktok: 'TikTok', linkedin: 'LinkedIn'}[oauth];

    if (connected && oauth === 'meta') {
      toast.success('Cuenta de Meta conectada — ahora elige tu Fan Page en el panel de abajo');
    } else if (connected && oauth === 'instagram') {
      if (params.get('long_lived') === '0') {
        toast.warning(
          'Instagram conectado con token corto — pide al Super Admin verificar el Instagram App Secret en Infraestructura'
        );
      } else {
        toast.success('Instagram conectado');
      }
    } else if (connected && oauth === 'tiktok') {
      toast.success('TikTok conectado');
    } else if (connected && oauth === 'linkedin') {
      toast.success('LinkedIn conectado');
    } else {
      const reason = params.get('reason');
      const credentialHint = oauth === 'instagram'
        ? ' revisa las credenciales de Instagram en Infraestructura (Instagram App ID/Secret, no los de Facebook).'
        : oauth === 'linkedin' && reason === 'linkedin_incomplete_auth'
        ? ' LinkedIn no pudo verificar tu identidad.'
        : ' inténtalo de nuevo o contacta a soporte si persiste.';
      toast.error(
        reason === 'expired'
          ? 'La sesión de conexión expiró — inténtalo de nuevo'
          : `No se pudo conectar ${label}. Intenta de nuevo;${credentialHint}`
      );
    }

    const openDetail = async () => {
      try {
        const { data: items } = await api.get('/tenant/integrations');
        setIntegrations(items);
        const match = items.find((i) => i.provider_key === providerKey);
        if (match) {
          const { data: d } = await api.get(`/tenant/integrations/${match.id}`);
          setDetail(d);
          setDetailId(match.id);
          setShowConnect(false);
        } else {
          const prov = providers.find((p) => p.key === providerKey)
            || (await api.get('/tenant/integrations/providers')).data.find((p) => p.key === providerKey);
          if (prov) {
            setShowConnect(true);
            setSelectedProvider(prov);
          }
        }
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    };
    openDetail();

    const url = new URL(window.location.href);
    ['oauth', 'connected', 'reason', 'long_lived', 'next'].forEach((k) => url.searchParams.delete(k));
    window.history.replaceState({}, '', url.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchData = async () => {
    try {
      const [iRes, pRes, mRes, planRes, lRes] = await Promise.all([
        api.get('/tenant/integrations'),
        api.get('/tenant/integrations/providers'),
        api.get('/tenant/channel-capabilities-matrix').catch(() => ({ data: [] })),
        api.get('/tenant/plan').catch(() => ({ data: null })),
        api.get('/tenant/locations').catch(() => ({ data: [] })),
      ]);
      setIntegrations(iRes.data);
      setProviders(pRes.data);
      setMatrix(mRes.data || []);
      setPlatformInfraEnabled(!!planRes.data?.entitlements?.platform_infrastructure_enabled);
      setBranches(lRes.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const handleUsePlatformInfrastructure = async (providerKey) => {
    setUsingPlatformInfra(providerKey);
    try {
      const { data } = await api.post(`/tenant/integrations/${providerKey}/use-platform-infrastructure`);
      toast.success(data?.mensaje || 'Conectado con la infraestructura de CavernicolApp');
      // El correo no termina aquí: hace falta que el dueño conecte su dominio
      // para que sus correos salgan a su nombre. Dejarlo en esta pantalla con
      // un «listo» sería mentirle — todavía no puede enviar nada.
      if (data?.siguiente) { navigate(data.siguiente); return; }
      fetchData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUsingPlatformInfra(null); }
  };

  useEffect(() => { fetchData(); }, []);

  const providerName = (key) => providers.find(p => p.key === key)?.name || key;

  const instanceForIntegration = (integration) => (
    matrix.find(m => m.base_integration_id === integration.id)
    || matrix.find(m => m.provider_key === integration.provider_key && m.instance_key === 'default')
  );

  // Toggle the "¿Qué hace este número?" panel. Auto-provisions a capability
  // instance on first expand so the owner never has to fill a separate
  // technical "create instance" form for their first/only number.
  const toggleChannelPanel = async (integration) => {
    if (expandedChannels === integration.id) { setExpandedChannels(null); return; }
    setExpandedChannels(integration.id);
    if (instanceForIntegration(integration)) return;

    setProvisioning(integration.id);
    try {
      await api.post(`/tenant/integrations/${integration.id}/ensure-instance`);
      const { data } = await api.get('/tenant/channel-capabilities-matrix');
      setMatrix(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setProvisioning(null);
    }
  };

  const toggleCapability = async (instanceId, capKey, enabled) => {
    const sKey = `${instanceId}-${capKey}`;
    setCapSaving(p => ({ ...p, [sKey]: true }));
    try {
      await api.put(`/tenant/integration-instances/${instanceId}/capabilities`, {
        capabilities: [{ capability_key: capKey, enabled }],
      });
      const { data } = await api.get('/tenant/channel-capabilities-matrix');
      setMatrix(data || []);
    } catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm('Esta combinación mezcla atención al cliente con avisos internos. ¿Confirmas?\n\nRecomendamos usar números separados.')) {
          try {
            await api.put(`/tenant/integration-instances/${instanceId}/capabilities`, {
              capabilities: [{ capability_key: capKey, enabled, explicit_risk_ack: true }],
            });
            const { data } = await api.get('/tenant/channel-capabilities-matrix');
            setMatrix(data || []);
          } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
        }
      } else {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    } finally {
      setCapSaving(p => ({ ...p, [sKey]: false }));
    }
  };

  // Open modal from redirect/state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('modal') === 'evolution' || location.state?.openEvolutionModal) {
      setShowEvolutionModal(true);
    }
  }, [location]);

  const fetchDetail = async (id) => {
    try {
      const { data } = await api.get(`/tenant/integrations/${id}`);
      setDetail(data);
      setDetailId(id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleSelectProvider = (provider) => {
    // Evolution API → open modal instead of credential form
    if (provider.key === 'evolution_api') {
      setShowEvolutionModal(true);
      setShowConnect(false);
      setSelectedProvider(null);
      return;
    }
    setSelectedProvider(provider);
    setCredForm({});
    setConnectBranchId('');
    setConnectInstanceLabel('');
    if (provider.category === 'voice_ai') {
      setAiPurposes(['voice_calls', 'general_default']);
    } else if (provider.category === 'ai') {
      setAiPurposes(['inbox_whatsapp', 'general_default']);
    }
  };

  const handleConnect = async () => {
    if (!selectedProvider) return;
    if (OAUTH_ONLY_PROVIDERS.has(selectedProvider.key)) {
      toast.error('Esta integración se conecta con OAuth — usa el botón de conexión de abajo.');
      return;
    }
    setSaving(true);
    try {
      const config = {};
      if (selectedProvider.category === 'ai' || selectedProvider.category === 'voice_ai') {
        config.ai_purposes = aiPurposes;
      }
      const { data } = await api.post('/tenant/integrations', {
        provider_key: selectedProvider.key,
        display_name: displayName || selectedProvider.name,
        credentials: credForm,
        config,
        ...(connectBranchId ? { branch_id: connectBranchId } : {}),
        ...(connectInstanceLabel ? { instance_label: connectInstanceLabel } : {}),
      });
      toast.success(`${selectedProvider.name} conectado`);
      setShowConnect(false);
      setSelectedProvider(null);
      setCredForm({});
      setDisplayName('');
      setConnectBranchId('');
      setConnectInstanceLabel('');
      await fetchData();
      // 2026-07-06 — providers that need an external webhook (Twilio Voice,
      // etc.) show that setup guide only in the detail view. Landing back on
      // the plain list after connecting meant the tenant had no way to know
      // that guide existed unless they happened to click back in — jump
      // straight to it instead.
      fetchDetail(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const { data } = await api.post(`/tenant/integrations/${id}/test`);
      toast[data.result === 'success' ? 'success' : 'error'](data.message);
      fetchData();
      if (detailId === id) fetchDetail(id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(null); }
  };

  const handleSaveCreds = async (id) => {
    const schema = detail?.credential_schema || [];
    const missing = schema.filter(f => f.required && !editCredForm[f.key]?.trim());
    if (missing.length > 0) {
      toast.error(`Completa todos los campos requeridos (no se puede editar solo uno — hay que volver a capturarlos todos para no borrar los demás)`);
      return;
    }
    setSavingCreds(true);
    try {
      await api.put(`/tenant/integrations/${id}`, { credentials: editCredForm });
      toast.success('Credenciales actualizadas');
      setEditingCreds(false);
      setEditCredForm({});
      fetchDetail(id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingCreds(false); }
  };

  const handleChangeBranch = async (id, branchId) => {
    setSavingBranch(true);
    try {
      await api.put(`/tenant/integrations/${id}`, { branch_id: branchId || '' });
      toast.success(branchId ? 'Sucursal asignada' : 'Ahora es el número predeterminado (todas las sucursales)');
      await fetchData();
      fetchDetail(id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingBranch(false); }
  };

  const handleDisconnect = async (id) => {
    if (!confirm('¿Desconectar esta integración?')) return;
    try {
      if (detail && detail.provider_key === 'meta_page') {
        await api.post('/tenant/oauth/meta/pages/disconnect');
        toast.success('Fan Page desconectada');
        setDetailId(null);
        load();
        return;
      }
      if (detail && detail.provider_key === 'meta_ads') {
        await api.post('/tenant/meta-ads/accounts/disconnect');
        toast.success('Cuenta publicitaria de Meta desconectada');
        setDetailId(null);
        load();
        return;
      }

      await api.delete(`/tenant/integrations/${id}`);
      toast.success('Integración desconectada');
      setDetailId(null);
      setDetail(null);
      fetchData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleIntegrationClick = (i) => {
    // Evolution API → modal
    if (i.provider_key === 'evolution_api') {
      setShowEvolutionModal(true);
      return;
    }
    fetchDetail(i.id);
  };

  if (loading) return <PantallaCargando />;

  // Detail view (non-evolution providers)
  if (detail && detailId) {
    const Icon = CATEGORY_ICONS[detail.provider_category] || Link2;
    return (
      <div data-testid="integration-detail">
        <button onClick={() => { setDetailId(null); setDetail(null); }} className="text-xs text-[#94A3B8] hover:text-[#0F172A] mb-3 block">&larr; Volver</button>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-[#0F172A] flex items-center justify-center"><Icon className="w-5 h-5 text-white" /></div>
          <div>
            <h1 className="text-xl font-bold text-[#0F172A]">{detail.display_name || detail.provider_name}</h1>
            <p className="text-xs text-[#64748B]">{providerName(detail.provider_key)} — {CATEGORY_LABELS[detail.provider_category] || detail.provider_category}</p>
          </div>
          <span className={`ml-auto text-xs px-2.5 py-1 rounded-full ${statusBadgeClass(detail.status)}`}>{statusLabel(detail.status)}</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {detail.status === 'pending_setup' && OAUTH_ONLY_PROVIDERS.has(detail.provider_key) && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm">
                Esta integración aún no está lista para recibir mensajes. Completa la conexión OAuth abajo — si creaste solo un nombre para mostrar, eso no basta.
              </div>
            )}
            {BRANCH_AWARE_CATEGORIES.includes(detail.provider_category) && branches.length > 1 && (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="integration-branch-assign">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-1">Sucursal</h3>
                <p className="text-xs text-[#64748B] mb-3">A qué sucursal atiende este número. "Todas las sucursales" es el predeterminado si un lead no tiene una sucursal específica con su propio número.</p>
                <select
                  value={detail.branch_id || ''}
                  disabled={savingBranch}
                  onChange={e => handleChangeBranch(detailId, e.target.value)}
                  className="w-full h-9 rounded-md border border-[#E2E8F0] px-3 text-sm text-[#0F172A]"
                  data-testid="detail-branch-select"
                >
                  <option value="">Todas las sucursales (predeterminado)</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
              <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Credenciales {editingCreds ? '' : '(enmascaradas)'}</h3>
              {!editingCreds ? (
                <>
                  <div className="space-y-2">
                    {Object.entries(detail.credentials_masked || {}).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between">
                        <span className="text-xs text-[#64748B]">{k}</span>
                        <code className="text-xs bg-[#F1F5F9] px-2 py-0.5 rounded font-mono">{v}</code>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-4 pt-3 border-t border-[#F1F5F9]">
                    <Button size="sm" variant="outline" onClick={() => handleTest(detailId)} disabled={testing === detailId} data-testid="test-connection-btn">
                      {testing === detailId ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <PlayCircle className="w-3.5 h-3.5 mr-1" />} Test
                    </Button>
                    {!OAUTH_ONLY_PROVIDERS.has(detail.provider_key) && (
                      <Button size="sm" variant="outline" onClick={() => { setEditingCreds(true); setEditCredForm({}); }} data-testid="edit-creds-btn">
                        Editar
                      </Button>
                    )}
                    {OAUTH_ONLY_PROVIDERS.has(detail.provider_key) && detail.provider_key !== 'meta_ads' ? (
                      <p className="text-xs text-[#94A3B8] flex items-center">
                        Para conectar, reautorizar o desconectar esta cuenta usa el panel de abajo, no este botón genérico.
                      </p>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => handleDisconnect(detailId)} className="text-red-500" data-testid="disconnect-btn">
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Desconectar
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-xs text-[#64748B] mb-3">Por seguridad no mostramos los valores guardados — tienes que volver a capturar todos los campos, no solo el que cambió.</p>
                  <div className="space-y-3">
                    {resolveCredentialSchema(detail).map(field => (
                      <FieldHelp key={field.key} label={`${field.label}${field.required ? ' *' : ''}`}
                        help={CREDENTIAL_FIELD_HELP[`${detail.provider_key}.${field.key}`]} htmlFor={`edit-cred-${field.key}`}>
                        <Input
                          id={`edit-cred-${field.key}`}
                          type={field.type === 'password' ? 'password' : 'text'}
                          placeholder={field.placeholder || ''}
                          value={editCredForm[field.key] || ''}
                          onChange={e => setEditCredForm(f => ({ ...f, [field.key]: e.target.value }))}
                          data-testid={`edit-cred-${field.key}`}
                        />
                      </FieldHelp>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-4 pt-3 border-t border-[#F1F5F9]">
                    <Button size="sm" onClick={() => handleSaveCreds(detailId)} disabled={savingCreds}
                      className="bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="save-creds-btn">
                      {savingCreds ? 'Guardando...' : 'Guardar'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditingCreds(false); setEditCredForm({}); }}>
                      Cancelar
                    </Button>
                  </div>
                </>
              )}
            </div>
            {detail.provider_key === 'twilio_voice' && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 space-y-3" data-testid="twilio-voice-setup-guide">
                <div>
                  <h3 className="text-sm font-semibold text-emerald-900 mb-1">Listo para el puente lead ↔ vendedor</h3>
                  <p className="text-xs text-emerald-800 leading-relaxed">
                    Con Account SID, Auth Token y el número de voz ya puedes: llamar saliente,
                    enlazar al vendedor y usar automatizaciones de llamada. <strong>No hace falta
                    pegar nada en Twilio</strong> solo para el puente.
                  </p>
                </div>
                <div className="bg-white/80 border border-emerald-100 rounded-lg p-3">
                  <p className="text-xs font-semibold text-[#0F172A] mb-1">¿Y el webhook amarillo de antes?</p>
                  <p className="text-[11px] text-[#475569] leading-relaxed mb-2">
                    Solo aplica si quieres que <em>alguien marque tu número Twilio</em> y conteste la IA
                    (llamadas entrantes). Eso se configura en <strong>Config IA → IA Voice</strong>, no aquí.
                    Si no activas entrantes, puedes ignorar la URL de abajo.
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="text-[10px] bg-[#F8FAFC] border border-[#E2E8F0] rounded px-2 py-1 flex-1 truncate text-[#64748B]">
                      {`${window.location.origin}${detail.webhook_url || '/api/webhooks/voice/inbound'}`}
                    </code>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}${detail.webhook_url || '/api/webhooks/voice/inbound'}`);
                      toast.success('URL copiada (solo para entrantes con IA)');
                    }} data-testid="copy-voice-webhook">Copiar</Button>
                  </div>
                  <a
                    href="/tenant/ai/voice-calls"
                    className="inline-block mt-2 text-xs font-semibold text-[#F72585] underline"
                    data-testid="goto-voice-ia"
                  >
                    Ir a IA Voice (entrantes y cerebro de la llamada) →
                  </a>
                </div>
              </div>
            )}
            {detail.provider_key === 'meta_whatsapp' && (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="meta-detail-embedded">
                <MetaWhatsappEmbeddedSignup
                  compact
                  onConnected={async (data) => {
                    await fetchData();
                    if (data?.id) fetchDetail(data.id);
                  }}
                />
                <p className="text-[11px] text-[#94A3B8] mt-2">
                  Si ya conectaste manualmente, no necesitas volver a usar este botón.
                </p>
              </div>
            )}
            {detail.provider_key === 'meta_page' && (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="meta-page-detail">
                <MetaFanPageConnect
                  compact
                  onConnected={async () => { await fetchData(); }}
                />
              </div>
            )}
            {detail.provider_key === 'instagram_login' && (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="instagram-login-detail">
                <InstagramLoginConnect
                  compact
                  onConnected={async () => { await fetchData(); }}
                />
              </div>
            )}
                        {detail.provider_key === 'meta_ads' && (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="meta-ads-detail">
                <p className="text-sm text-[#0F172A] mb-3">
                  Cuenta publicitaria de Meta (Ads). Usa tu login de Meta; aqui se elige la cuenta de anuncios del negocio.
                </p>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDisconnect(detailId)} data-testid="meta-ads-disconnect-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Desconectar cuenta publicitaria
                </Button>
              </div>
            )}
{detail.provider_key === 'tiktok_login' && (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="tiktok-login-detail">
                <TikTokLoginConnect
                  compact
                  onConnected={async () => { await fetchData(); }}
                />
              </div>
            )}
            {detail.provider_key === 'linkedin_login' && (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="linkedin-login-detail">
                <LinkedInLoginConnect
                  compact
                  onConnected={async () => { await fetchData(); }}
                />
              </div>
            )}
            {detail.provider_key === 'linkedin_page' && (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="linkedin-page-detail">
                <LinkedInPageConnect
                  compact
                  onConnected={async () => { await fetchData(); }}
                />
              </div>
            )}
            {webhookPathFor(detail) && detail.provider_key !== 'twilio_voice' && (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="integration-webhook-url">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-2">Webhook URL</h3>
                {WEBHOOK_SETUP_GUIDES[detail.provider_key] && (
                  <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm font-semibold text-amber-800 mb-2">{WEBHOOK_SETUP_GUIDES[detail.provider_key].title}</p>
                    <ol className="text-xs text-amber-700 list-decimal list-inside space-y-1">
                      {WEBHOOK_SETUP_GUIDES[detail.provider_key].steps.map((s, idx) => <li key={idx}>{s}</li>)}
                    </ol>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <code className="text-[10px] bg-[#F1F5F9] px-2 py-1 rounded flex-1 break-all">
                    {`${window.location.origin}${webhookPathFor(detail)}`}
                  </code>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}${webhookPathFor(detail)}`);
                    toast.success('URL copiada');
                  }} data-testid="copy-detail-webhook">Copiar</Button>
                </div>
              </div>
            )}
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 h-fit">
            <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Logs Recientes</h3>
            {(detail.recent_logs || []).length === 0 ? (
              <p className="text-xs text-[#94A3B8] text-center py-3">Sin logs</p>
            ) : (
              <div className="space-y-2">
                {detail.recent_logs.map(l => (
                  <div key={l.id} className="border-l-2 border-[#E2E8F0] pl-3 py-1">
                    <p className="text-xs font-medium text-[#0F172A]">{l.action}</p>
                    <p className="text-[11px] text-[#94A3B8]">{l.status} {l.duration_ms ? `(${l.duration_ms}ms)` : ''}</p>
                    <p className="text-[10px] text-[#CBD5E1]">{l.created_at ? new Date(l.created_at).toLocaleString() : ''}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Connect form (non-evolution providers)
  if (showConnect) {
    const categories = [...new Set(providers.filter(p => p.status === 'active').map(p => p.category))];
    const filtered = providers.filter(p => p.status === 'active' && (!categoryFilter || p.category === categoryFilter));

    if (selectedProvider) {
      return (
        <div data-testid="integration-connect-form">
          <button onClick={() => setSelectedProvider(null)} className="text-xs text-[#94A3B8] hover:text-[#0F172A] mb-3 block">&larr; Elegir otro provider</button>
          <h2 className="text-xl font-bold text-[#0F172A] mb-4">Conectar {selectedProvider.name}</h2>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 space-y-4">
            <div>
              <label className="text-xs font-medium text-[#64748B] mb-1 block">Nombre para mostrar</label>
              <Input value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder={selectedProvider.name} data-testid="integration-display-name" />
            </div>
            {selectedProvider.key === 'meta_whatsapp' && (
              <MetaWhatsappEmbeddedSignup
                branchId={connectBranchId}
                displayName={displayName}
                onConnected={async (data) => {
                  setShowConnect(false);
                  setSelectedProvider(null);
                  setCredForm({});
                  setDisplayName('');
                  setConnectBranchId('');
                  await fetchData();
                  if (data?.id) fetchDetail(data.id);
                }}
              />
            )}
            {selectedProvider.key === 'meta_whatsapp' && (
              <p className="text-xs text-[#94A3B8] text-center">— o conecta manualmente —</p>
            )}
            {selectedProvider.key === 'meta_page' && (
              <MetaFanPageConnect
                branchId={connectBranchId}
                onConnected={async () => {
                  setShowConnect(false);
                  setSelectedProvider(null);
                  setCredForm({});
                  setDisplayName('');
                  setConnectBranchId('');
                  await fetchData();
                }}
              />
            )}
            {selectedProvider.key === 'instagram_login' && (
              <InstagramLoginConnect
                onConnected={async () => {
                  setShowConnect(false);
                  setSelectedProvider(null);
                  setCredForm({});
                  setDisplayName('');
                  setConnectBranchId('');
                  await fetchData();
                }}
              />
            )}
            {selectedProvider.key === 'tiktok_login' && (
              <TikTokLoginConnect
                onConnected={async () => {
                  setShowConnect(false);
                  setSelectedProvider(null);
                  setCredForm({});
                  setDisplayName('');
                  setConnectBranchId('');
                  await fetchData();
                }}
              />
            )}
            {selectedProvider.key === 'linkedin_login' && (
              <LinkedInLoginConnect
                onConnected={async () => {
                  setShowConnect(false);
                  setSelectedProvider(null);
                  setCredForm({});
                  setDisplayName('');
                  setConnectBranchId('');
                  await fetchData();
                }}
              />
            )}
            {selectedProvider.key === 'linkedin_page' && (
              <LinkedInPageConnect
                onConnected={async () => {
                  setShowConnect(false);
                  setSelectedProvider(null);
                  setCredForm({});
                  setDisplayName('');
                  setConnectBranchId('');
                  await fetchData();
                }}
              />
            )}
            {resolveCredentialSchema(selectedProvider).map(field => (
              <FieldHelp key={field.key} label={`${field.label}${field.required ? ' *' : ''}`}
                help={CREDENTIAL_FIELD_HELP[`${selectedProvider.key}.${field.key}`]} htmlFor={`cred-${field.key}`}>
                <Input
                  id={`cred-${field.key}`}
                  type={field.type === 'password' ? 'password' : 'text'}
                  placeholder={field.placeholder || ''}
                  value={credForm[field.key] || ''}
                  onChange={e => setCredForm(f => ({ ...f, [field.key]: e.target.value }))}
                  data-testid={`cred-${field.key}`}
                />
              </FieldHelp>
            ))}
            {MULTI_INSTANCE_PROVIDER_KEYS.includes(selectedProvider.key)
              && integrations.some(i => i.provider_key === selectedProvider.key) && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="multi-instance-label-box">
                <p className="text-sm font-semibold text-[#0F172A] mb-1">Ya tienes {providerName(selectedProvider.key)} conectado</p>
                <p className="text-xs text-[#64748B] mb-2">
                  Si estos datos son de un número/cuenta distinto, dale un nombre para agregarlo como una conexión
                  adicional (por ejemplo "Avisos internos") en vez de reemplazar el que ya tienes. Si lo dejas en blanco,
                  se sobrescribirá tu conexión actual.
                </p>
                <Input value={connectInstanceLabel} onChange={e => setConnectInstanceLabel(e.target.value)}
                  placeholder="Ej. Avisos internos" data-testid="connect-instance-label" />
              </div>
            )}
            {BRANCH_AWARE_CATEGORIES.includes(selectedProvider.category) && branches.length > 1 && (
              <div>
                <label className="text-xs font-medium text-[#64748B] mb-1 block">Sucursal</label>
                <select
                  value={connectBranchId}
                  onChange={e => setConnectBranchId(e.target.value)}
                  className="w-full h-9 rounded-md border border-[#E2E8F0] px-3 text-sm text-[#0F172A]"
                  data-testid="connect-branch-select"
                >
                  <option value="">Todas las sucursales (predeterminado)</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                <p className="text-[11px] text-[#94A3B8] mt-1">Si eliges una sucursal, este número solo atenderá leads de esa sucursal — el resto sigue usando el número predeterminado.</p>
              </div>
            )}
            {(selectedProvider.category === 'ai' || selectedProvider.category === 'voice_ai') && (
              <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4 space-y-2" data-testid="ai-purpose-picker">
                <p className="text-sm font-semibold text-[#0F172A]">¿Para qué usarás este proveedor?</p>
                <p className="text-xs text-[#64748B]">Marca los usos. Así la IA sabe cuándo responder WhatsApp, llamadas, etc.</p>
                {(AI_PURPOSES_BY_CATEGORY[selectedProvider.category] || Object.keys(AI_PURPOSE_LABELS)).map((key) => (
                  <label key={key} className="flex items-center gap-2 text-sm text-[#334155] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={aiPurposes.includes(key)}
                      onChange={(e) => {
                        setAiPurposes((prev) => e.target.checked
                          ? [...prev, key]
                          : prev.filter((p) => p !== key));
                      }}
                      className="rounded border-[#CBD5E1]"
                    />
                    {AI_PURPOSE_LABELS[key] || key}
                  </label>
                ))}
              </div>
            )}
            {!OAUTH_ONLY_PROVIDERS.has(selectedProvider.key) && (
              <div className="flex justify-end gap-3 pt-4 border-t border-[#E2E8F0]">
                <Button variant="outline" onClick={() => { setShowConnect(false); setSelectedProvider(null); }}>Cancelar</Button>
                <Button onClick={handleConnect} disabled={saving} className="bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="connect-btn">
                  {saving ? 'Conectando...' : 'Conectar'}
                </Button>
              </div>
            )}
            {OAUTH_ONLY_PROVIDERS.has(selectedProvider.key) && (
              <div className="flex justify-end gap-3 pt-4 border-t border-[#E2E8F0]">
                <Button variant="outline" onClick={() => { setShowConnect(false); setSelectedProvider(null); }}>Cerrar</Button>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div data-testid="provider-selector">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#0F172A]">Elegir Provider</h2>
          <Button variant="outline" size="sm" onClick={() => setShowConnect(false)}><X className="w-4 h-4" /></Button>
        </div>
        <div className="flex gap-1.5 mb-4 flex-wrap">
          <button onClick={() => setCategoryFilter('')} className={`px-3 py-1 text-xs rounded-full border ${!categoryFilter ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0] text-[#64748B]'}`}>Todos</button>
          {categories.map(c => (
            <button key={c} onClick={() => setCategoryFilter(c)} className={`px-3 py-1 text-xs rounded-full border ${categoryFilter === c ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0] text-[#64748B]'}`}>
              {CATEGORY_LABELS[c] || c}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(p => {
            const Icon = CATEGORY_ICONS[p.category] || Link2;
            return (
              <button key={p.key} onClick={() => handleSelectProvider(p)} data-testid={`select-provider-${p.key}`}
                className="text-left p-4 bg-white border border-[#E2E8F0] rounded-xl hover:border-[#0F172A] transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#F1F5F9] flex items-center justify-center"><Icon className="w-4 h-4 text-[#475569]" /></div>
                  <div>
                    <p className="text-sm font-medium text-[#0F172A]">{p.name}</p>
                    <p className="text-[11px] text-[#94A3B8]">{p.category}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#CBD5E1] ml-auto" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Main list
  return (
    <>
      <div data-testid="integration-center">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight">Integraciones</h1>
            <p className="text-sm text-[#64748B] mt-1">{integrations.length} integraciones configuradas</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.location.href = '/tenant/integrations/purposes'}
              className="text-[#475569] border-[#E2E8F0]" data-testid="channel-purposes-btn">
              <Settings className="w-4 h-4 mr-2" /> Capacidades de canales
            </Button>
            <Button onClick={() => setShowConnect(true)} className="bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="add-integration-btn">
              <Plus className="w-4 h-4 mr-2" /> Conectar
            </Button>
          </div>
        </div>

        <div className="mb-4 p-4 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="attendance-flow-cta">
          <div>
            <p className="text-sm font-semibold text-[#0F172A]">¿Para qué sirve cada integración?</p>
            <p className="text-xs text-[#64748B]">Configura el flujo completo (WhatsApp → IA → vendedor) y revisa qué incluye tu plan.</p>
          </div>
          <Button variant="outline" onClick={() => window.location.href = '/tenant/setup'} className="border-[#F72585] text-[#F72585] hover:bg-[#F72585]/5">
            <Zap className="w-4 h-4 mr-2" /> Atención automática
          </Button>
        </div>

        {platformInfraEnabled && (
          <div className="mb-4 p-4 rounded-xl border border-[#F72585]/20 bg-[#F72585]/5" data-testid="platform-infrastructure-banner">
            <p className="text-sm font-semibold text-[#0F172A] mb-1">Tu plan incluye la infraestructura de CavernicolApp</p>
            <p className="text-xs text-[#64748B] mb-3">No necesitas tu propia cuenta de WhatsApp/Voz/Email — usa la nuestra. Ese canal deja de tener tu propio número/remitente, pero queda funcionando de inmediato.</p>
            <div className="flex flex-wrap gap-2">
              {['twilio_whatsapp', 'twilio_voice', 'email'].filter(pk => !integrations.some(i => i.provider_key === pk)).map(pk => (
                <Button key={pk} size="sm" variant="outline" disabled={usingPlatformInfra === pk}
                  onClick={() => handleUsePlatformInfrastructure(pk)} data-testid={`use-platform-infra-${pk}`}>
                  {usingPlatformInfra === pk ? 'Conectando...' : `Usar ${providerName(pk) || pk} de CavernicolApp`}
                </Button>
              ))}
            </div>
          </div>
        )}

        {integrations.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-12 text-center">
            <Link2 className="w-12 h-12 text-[#CBD5E1] mx-auto mb-3" />
            <p className="text-sm text-[#64748B]">Sin integraciones configuradas</p>
            <Button onClick={() => setShowConnect(true)} variant="outline" size="sm" className="mt-4">Conectar primera integración</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {integrations.map(i => {
              const Icon = CATEGORY_ICONS[i.provider_category] || Link2;
              const hasCapabilities = supportsCapabilityPanel(i);
              const isExpanded = expandedChannels === i.id;
              const inst = instanceForIntegration(i);
              const enabledCaps = new Set((inst?.capabilities || []).filter(c => c.is_enabled).map(c => c.capability_key));
              return (
                <div key={i.id} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden" data-testid={`integration-${i.id}`}>
                  <div className="p-4 flex items-center justify-between cursor-pointer hover:border-[#0F172A]/30 transition-all"
                    onClick={() => handleIntegrationClick(i)}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#F1F5F9] flex items-center justify-center"><Icon className="w-4 h-4 text-[#475569]" /></div>
                      <div>
                        <p className="text-sm font-medium text-[#0F172A]">{i.display_name || i.provider_name}</p>
                        <p className="text-xs text-[#94A3B8]">
                          {providerName(i.provider_key)}
                          {BRANCH_AWARE_CATEGORIES.includes(i.provider_category) && branches.length > 1 && (
                            <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#64748B]">
                              {i.branch_name || 'Todas las sucursales'}
                            </span>
                          )}
                          {i.instance_label && i.instance_label !== 'default' && (
                            <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
                              title="Esta es una conexión adicional del mismo proveedor">
                              {i.instance_label}
                            </span>
                          )}
                        </p>
                        {(i.config?.ai_purposes || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {i.config.ai_purposes.map((p) => (
                              <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#64748B]">
                                {AI_PURPOSE_LABELS[p] || p}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {i.status === 'connected' ? <Wifi className="w-4 h-4 text-emerald-500" /> : <WifiOff className="w-4 h-4 text-[#CBD5E1]" />}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(i.status)}`}>{statusLabel(i.status)}</span>
                      {i.provider_key !== 'evolution_api' && (
                        <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleTest(i.id); }} disabled={testing === i.id}>
                          {testing === i.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                        </Button>
                      )}
                      {hasCapabilities && i.status === 'connected' && (
                        <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); toggleChannelPanel(i); }} data-testid={`toggle-channel-panel-${i.id}`} title="Capacidades del canal">
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-[#64748B]" /> : <ChevronRight className="w-4 h-4 text-[#CBD5E1]" />}
                        </Button>
                      )}
                    </div>
                  </div>

                  {hasCapabilities && isExpanded && (
                    <div className="border-t border-[#F1F5F9] bg-[#F8FAFC] p-4" onClick={e => e.stopPropagation()} data-testid={`channel-panel-${i.id}`}>
                      {WEBHOOK_SETUP_GUIDES[i.provider_key] && (
                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`webhook-guide-${i.id}`}>
                          <p className="text-sm font-semibold text-amber-800 mb-2">{WEBHOOK_SETUP_GUIDES[i.provider_key].title}</p>
                          <ol className="text-xs text-amber-700 list-decimal list-inside space-y-1 mb-2">
                            {WEBHOOK_SETUP_GUIDES[i.provider_key].steps.map((s, idx) => <li key={idx}>{s}</li>)}
                          </ol>
                          {webhookPathFor(i) && (
                            <div className="flex items-center gap-2">
                              <code className="text-[10px] bg-white border border-amber-200 rounded px-2 py-1 flex-1 truncate">
                                {`${window.location.origin}${webhookPathFor(i)}`}
                              </code>
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
                                navigator.clipboard.writeText(`${window.location.origin}${webhookPathFor(i)}`);
                                toast.success('URL copiada');
                              }} data-testid={`copy-webhook-${i.id}`}>Copiar</Button>
                            </div>
                          )}
                        </div>
                      )}
                      <p className="text-sm font-semibold text-[#0F172A] mb-1">
                        {i.provider_category === 'whatsapp' ? '¿Qué hace este número?' : '¿Qué hace esta conexión?'}
                      </p>
                      <p className="text-xs text-[#64748B] mb-3">Marca las funciones que quieres activar para {i.display_name || i.provider_name}.</p>
                      {inst?.suggest_split && MULTI_INSTANCE_PROVIDER_KEYS.includes(i.provider_key) && (
                        <div className="mb-3 p-3 rounded-lg border border-[#F72585]/20 bg-[#F72585]/5" data-testid={`suggest-split-${i.id}`}>
                          <p className="text-sm font-semibold text-[#0F172A] mb-1">Sugerencia: separa este número en dos</p>
                          <p className="text-xs text-[#64748B] mb-2">
                            Este número está haciendo dos trabajos a la vez: atendiendo clientes y avisando a tu equipo.
                            Es mejor usar un número solo para clientes y otro solo para avisos internos — llegan más
                            rápido los avisos y no se mezclan con las conversaciones de venta.
                          </p>
                          <Button size="sm" variant="outline" className="border-[#F72585] text-[#F72585] hover:bg-[#F72585]/10"
                            onClick={(e) => { e.stopPropagation(); setShowConnect(true); handleSelectProvider(providers.find(p => p.key === i.provider_key) || { key: i.provider_key, name: providerName(i.provider_key), category: i.provider_category }); }}
                            data-testid={`suggest-split-action-${i.id}`}>
                            <Plus className="w-3.5 h-3.5 mr-1.5" /> Agregar número para avisos internos
                          </Button>
                        </div>
                      )}
                      {provisioning === i.id ? (
                        <div className="flex items-center gap-2 text-xs text-[#94A3B8] py-2"><Loader2 className="w-4 h-4 animate-spin" /> Preparando...</div>
                      ) : !inst ? (
                        <p className="text-xs text-[#94A3B8] py-2">No se pudo preparar este número. Intenta de nuevo.</p>
                      ) : (
                        <div className="space-y-1">
                          {(inst.available_capabilities || []).map(avail => {
                            const isEnabled = enabledCaps.has(avail.capability_key);
                            const sKey = `${inst.id}-${avail.capability_key}`;
                            // Hotfix (07-18): WhatsApp Evolution es una API no oficial —
                            // usarla para campañas masivas arriesga que Meta banee el
                            // número real del negocio. El backend ya lo bloquea; aquí lo
                            // explicamos antes de que el tenant intente activarlo.
                            const blockedForEvolution = i.provider_key === 'evolution_api'
                              && avail.capability_key === 'outbound_campaign_whatsapp';
                            return (
                              <label key={avail.capability_key}
                                className={`flex items-start gap-3 p-2 rounded-lg ${blockedForEvolution ? 'opacity-60' : 'hover:bg-white cursor-pointer'}`}
                                data-testid={`cap-toggle-${i.id}-${avail.capability_key}`}>
                                <Checkbox checked={isEnabled} disabled={capSaving[sKey] || blockedForEvolution}
                                  onCheckedChange={(checked) => toggleCapability(inst.id, avail.capability_key, !!checked)}
                                  className="mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-[#0F172A]">{avail.ui_label}</span>
                                    <Badge variant="outline" className="text-[10px] text-[#94A3B8] border-[#E2E8F0]">
                                      {DIRECTION_LABELS[avail.direction] || avail.direction}
                                    </Badge>
                                    {capSaving[sKey] && <Loader2 className="h-3 w-3 animate-spin text-[#94A3B8]" />}
                                  </div>
                                  <p className="text-xs text-[#64748B] mt-0.5">
                                    {blockedForEvolution
                                      ? 'No disponible en Evolution: enviar campañas masivas por aquí puede hacer que Meta banee este número. Conecta Twilio o Gupshup para campañas.'
                                      : avail.help_text}
                                  </p>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <EvolutionConnectModal
        open={showEvolutionModal}
        onOpenChange={setShowEvolutionModal}
        onConnected={() => { setShowEvolutionModal(false); fetchData(); }}
      />
    </>
  );
}
